import { createContext, useCallback, useContext, useMemo, useState } from "react";

/**
 * UIContext — cross-cutting UI state that does not belong to any single
 * feature: toast notifications, a global loading flag, and the identifier
 * of whichever modal/bottom-sheet is currently open.
 *
 * The actual <Toast /> and <BottomSheet /> presentational components are
 * a Phase 3 deliverable (reusable components/layouts); this context only
 * defines the state contract they will consume.
 */

const UIContext = createContext(undefined);

let toastCounter = 0;

export function UIProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const [activeModal, setActiveModal] = useState(null);
  const [isGlobalLoading, setIsGlobalLoading] = useState(false);

  const showToast = useCallback((message, { type = "info", duration = 2500 } = {}) => {
    const id = ++toastCounter;
    setToasts((current) => [...current, { id, message, type }]);
    setTimeout(() => {
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, duration);
    return id;
  }, []);

  const dismissToast = useCallback((id) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const openModal = useCallback((modalId) => setActiveModal(modalId), []);
  const closeModal = useCallback(() => setActiveModal(null), []);

  const value = useMemo(
    () => ({
      toasts,
      showToast,
      dismissToast,
      activeModal,
      openModal,
      closeModal,
      isGlobalLoading,
      setIsGlobalLoading,
    }),
    [toasts, showToast, dismissToast, activeModal, openModal, closeModal, isGlobalLoading]
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI() {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error("useUI must be used within a UIProvider");
  }
  return context;
}
